/**
 * Auth Context — GenesisBox
 *
 * Token-based auth: stores a Firebase ID token (or localhost-only Basic token)
 * in localStorage. API calls refresh the Firebase token from the client SDK.
 */

import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import type { AuthContextType } from '@/types/auth.types';
import { firebaseSignOutAll } from '@config/firebase';
import { clearActiveConnectionId, clearActiveOrgId } from '@services/storage/savedConnectionsStorage';
import { AUTH_SESSION_EXPIRED_EVENT } from '@services/api/apiConfig';

const TOKEN_KEY = 'genesisbox:jwt';
const USER_KEY = 'genesisbox:user';
const ORG_KEY = 'genesisbox:org';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<string | null>(() => localStorage.getItem(USER_KEY));

  const isAuthenticated = !!token;

  /**
   * Login accepts either:
   * - Firebase ID token (confirmed by /auth/firebase) — pass as
   *   first arg, leave password empty
   * - Dev username + password (Basic Auth fallback)
   */
  const login = useCallback(async (tokenOrUsername: string, password: string) => {
    if (!password) {
      localStorage.setItem(TOKEN_KEY, tokenOrUsername);
      setToken(tokenOrUsername);
      try {
        const payload = JSON.parse(atob(tokenOrUsername.split('.')[1]));
        const nextUser = payload.name || payload.sub || 'User';
        localStorage.setItem(USER_KEY, nextUser);
        setUser(nextUser);
      } catch {
        setUser('User');
      }
      return;
    }

    const basicToken = btoa(`${tokenOrUsername}:${password}`);
    localStorage.setItem(TOKEN_KEY, `basic:${basicToken}`);
    localStorage.setItem(USER_KEY, tokenOrUsername);
    setToken(`basic:${basicToken}`);
    setUser(tokenOrUsername);
  }, []);

  const logout = useCallback(() => {
    void firebaseSignOutAll().catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ORG_KEY);
    clearActiveConnectionId();
    clearActiveOrgId();
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const onSessionExpired = () => logout();
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [logout]);

  useEffect(() => {
    const onStorage = () => {
      setToken(localStorage.getItem(TOKEN_KEY));
      setUser(localStorage.getItem(USER_KEY));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext };
